// src/pages/treatments/TreatmentTemplateForDental.jsx
import React, { useState, useEffect } from "react";
import ConsultationModal from "@/components/ConsultationModal";
import { Button } from "@/components/ui/button";
import {
  CheckCircle, Clock, Star, Users, Shield, ArrowRight, Phone,
  ChevronDown, ChevronUp, Award, Heart, MapPin, Mail, Facebook,
  Twitter, Instagram, Youtube, Menu, X, Calendar, ThumbsUp,
  Activity, Plane, MessageCircle, FileText, Video, Building2,
  HeartPulse, Microscope, Stethoscope, BadgeCheck, Globe, Timer,
  DollarSign, TrendingDown, Sparkles, Send, Zap, Lock,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import axios from "axios";
import PropTypes from "prop-types";

// Constants
const WHATSAPP_NUMBER = "917289007800";
const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}/api`;

const treatmentOptions = [
  "Cardiology", "Orthopedics", "Oncology", "Neurology & Spine",
  "Organ Transplant", "Dental Care", "Skin Care & Dermatology",
  "Cosmetic Surgery", "IVF & Fertility", "Bariatric Surgery",
  "Eye Care", "ENT & Cochlear", "Ayurveda & Wellness",
  "Hair Transplant", "Other",
];

const countries = [
  "Afghanistan", "Bahrain", "Bangladesh", "Cameroon", "Egypt",
  "Ethiopia", "Ghana", "India", "Indonesia", "Iraq", "Kenya",
  "Kuwait", "Malaysia", "Mauritius", "Nigeria", "Oman", "Pakistan",
  "Qatar", "Rwanda", "Saudi Arabia", "Singapore", "Somalia",
  "South Africa", "Sri Lanka", "Sudan", "Tanzania", "Thailand",
  "UAE", "Uganda", "UK", "USA", "Vietnam", "Yemen", "Zambia",
  "Zimbabwe", "Other",
];

const PROCESS_STEPS = [
  {
    num: 1,
    title: "Send your reports — free review",
    desc: "WhatsApp your medical reports to PaxGrow. Within 48 hours, Samar personally sends a written summary: condition, treatment options, cost estimate, and recommended hospital. No charge. No commitment.",
    icon: FileText,
    color: "from-orange-400 to-orange-600",
  },
  {
    num: 2,
    title: "Free video consultation with your surgeon",
    desc: "PaxGrow arranges a direct video call between you and your treating specialist in India. You speak to the surgeon before committing to anything. Formal treatment plan and cost estimate provided in writing.",
    icon: Video,
    color: "from-blue-400 to-blue-600",
  },
  {
    num: 3,
    title: "Visa, flights, and accommodation arranged",
    desc: "PaxGrow provides your hospital visa invitation letter within 3 days of confirming your case. We guide you through the e-Medical Visa process (Kenya: 24–72hrs), and arrange accommodation near your hospital.",
    icon: Plane,
    color: "from-violet-400 to-violet-600",
  },
  {
    num: 4,
    title: "Airport pickup and full in-hospital support",
    desc: "PaxGrow coordinator meets you at the airport, accompanies you to the hospital, and is available throughout your stay. Translation, daily updates to family at home, and discharge documentation all handled.",
    icon: Building2,
    color: "from-green-400 to-green-600",
  },
  {
    num: 5,
    title: "90-day follow-up after you return home",
    desc: "PaxGrow maintains contact for 90 days after your return. Two telemedicine follow-up sessions with your Indian surgeon are included. If anything concerns you, we connect you with your treating team immediately.",
    icon: HeartPulse,
    color: "from-rose-400 to-rose-600",
  },
];

const DEFAULT_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%23374151'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239CA3AF' font-size='14'%3E👤%3C/text%3E%3C/svg%3E";

const toast = {
  success: (msg) => console.log("✅", msg),
  error: (msg) => console.error("❌", msg),
  info: (msg) => console.log("ℹ️", msg),
};

// ============================================
// HERO SCREEN
// ============================================
const HeroScreen = ({ treatment }) => {
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "",
    treatment: treatment.name || "", message: "",
  });

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.email.trim()) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = "Email is invalid";
    if (!form.phone.trim()) errors.phone = "Phone is required";
    else if (form.phone.length < 8) errors.phone = "Phone number is too short";
    if (!form.country) errors.country = "Country is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, form);
      if (window.gtag) {
        window.gtag('event', 'consultation_request', {
          'treatment': form.treatment,
          'country': form.country,
          'event_category': 'lead',
          'event_label': form.treatment
        });
      }
      const whatsappMessage = encodeURIComponent(
        `Hi, I just submitted a consultation request.\n\nName: ${form.name}\nPhone: ${form.phone}\nTreatment: ${form.treatment}\nCountry: ${form.country}`
      );
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;
      navigate("/thank-you", { state: { formData: form, whatsappUrl } });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Submission failed. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${treatment.name} Treatment in India | PaxGrow Medical Tourism`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', treatment.description);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = treatment.description;
      document.head.appendChild(meta);
    }
  }, [treatment]);

  return (
    <section className="relative min-h-screen flex items-center bg-slate-900 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${treatment.heroImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/97 via-slate-900/85 to-slate-900/50" />
      <div className="absolute top-20 right-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* 🆕 BACK TO TREATMENTS BUTTON - TOP LEFT */}
      <button
        onClick={() => navigate("/treatments")}
        className="absolute top-6 left-4 sm:left-6 lg:left-8 z-20 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 hover:border-white/40 text-white rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 hover:-translate-x-1 group"
        data-testid="back-to-treatments-btn"
        aria-label="Back to all treatments"
      >
        <ArrowLeft className="w-4 h-4 group-hover:scale-110 transition-transform" />
        <span className="hidden sm:inline">Back to Treatments</span>
        <span className="sm:hidden">Back</span>
      </button>


      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* LEFT */}
          <div className="lg:sticky lg:top-20">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 text-orange-300 rounded-full px-4 py-1.5 text-xs font-semibold mb-5">
              <Award className="w-3.5 h-3.5" />
              {treatment.category} · {treatment.badge}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-3">
              {treatment.name}
            </h1>
            <p className="text-xl text-orange-400 font-semibold mb-3">{treatment.tagline}</p>
            <div className="inline-flex items-center gap-2 bg-green-500/15 border border-green-500/30 text-white rounded-xl px-4 py-2.5 text-sm font-medium mb-5">
              <TrendingDown className="w-4 h-4 text-white shrink-0" />
              {treatment.heroSubtag}
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-6 max-w-lg">{treatment.description}</p>
            <div className="flex flex-wrap gap-6 mb-6">
              {treatment.stats?.slice(0, 2).map((stat, i) => (
                <div key={i} className="text-white">
                  <div className="text-2xl font-bold text-orange-400">{stat.value}</div>
                  <div className="text-xs text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white rounded-full px-7 py-4 font-semibold text-sm transition-all shadow-lg shadow-green-500/30 hover:shadow-green-500/50 hover:-translate-y-0.5 mb-6"
            >
              <MessageCircle className="w-5 h-5 fill-white" />
              Send Reports — Free 48hr Review
            </a>
            <div className="grid grid-cols-2 gap-2 mb-6 max-w-xs">
              {[
                { icon: DollarSign, label: "Zero coordination fees", color: "text-green-400" },
                { icon: Timer, label: "Reply within 48 hours", color: "text-blue-400" },
                { icon: BadgeCheck, label: "JCI & NABH hospitals", color: "text-violet-400" },
                { icon: Plane, label: "Visa guidance included", color: "text-orange-400" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <b.icon className={`w-3.5 h-3.5 shrink-0 ${b.color}`} />
                  <span className="text-slate-300 text-xs">{b.label}</span>
                </div>
              ))}
            </div>
            {treatment.costCard && (
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-4 max-w-xs">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <TrendingDown className="w-3.5 h-3.5 text-orange-400" />
                  </div>
                  <span className="text-white/60 text-xs font-medium">{treatment.costCard.label}</span>
                </div>
                <div className="text-2xl font-bold text-orange-400 leading-none mb-1">{treatment.costCard.value}</div>
                <div className="text-white/40 text-xs">{treatment.costCard.compare}</div>
              </div>
            )}
          </div>

          {/* RIGHT — FORM */}
          <div className="lg:sticky lg:top-20">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl">Free Consultation</h3>
                    <p className="text-orange-100 text-xs">Expert advice · No commitment · 48hr reply</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2 mt-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((_, i) => (
                      <div key={i} className="w-6 h-6 rounded-full border-2 border-orange-500 bg-slate-300 flex items-center justify-center text-xs text-white">
                        👤
                      </div>
                    ))}
                  </div>
                  <span className="text-orange-100 text-xs">
                    <strong className="text-white">127 patients</strong> consulted this week
                  </span>
                </div>
              </div>
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-slate-700 text-sm font-semibold">Full Name *</Label>
                    <Input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your full name"
                      className={`mt-1 rounded-xl border-slate-200 focus:border-orange-400 focus:ring-orange-400 ${formErrors.name ? 'border-red-500' : ''}`}
                    />
                    {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 text-sm font-semibold">Email *</Label>
                      <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="your@email.com"
                        className={`mt-1 rounded-xl border-slate-200 focus:border-orange-400 ${formErrors.email ? 'border-red-500' : ''}`}
                      />
                      {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 text-sm font-semibold">Phone *</Label>
                      <Input
                        required
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+1 234 567 890"
                        className={`mt-1 rounded-xl border-slate-200 focus:border-orange-400 ${formErrors.phone ? 'border-red-500' : ''}`}
                      />
                      {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 text-sm font-semibold">Country *</Label>
                      <Select onValueChange={(v) => setForm({ ...form, country: v })}>
                        <SelectTrigger className={`mt-1 rounded-xl border-slate-200 ${formErrors.country ? 'border-red-500' : ''}`}>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formErrors.country && <p className="text-red-500 text-xs mt-1">{formErrors.country}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 text-sm font-semibold">Treatment *</Label>
                      <Select value={form.treatment} onValueChange={(v) => setForm({ ...form, treatment: v })}>
                        <SelectTrigger className="mt-1 rounded-xl border-slate-200">
                          <SelectValue placeholder="Select treatment" />
                        </SelectTrigger>
                        <SelectContent>
                          {treatmentOptions.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm font-semibold">
                      Medical Details <span className="text-slate-400 font-normal">(Optional)</span>
                    </Label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Briefly describe your condition..."
                      rows={3}
                      className="mt-1 rounded-xl border-slate-200 focus:border-orange-400"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-bold h-12 shadow-md shadow-orange-200 text-sm"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    {loading ? "Submitting..." : "Get My Free Consultation"}
                  </Button>
                  <div className="flex items-center justify-center gap-4 pt-1">
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Lock className="w-3 h-3 text-slate-400" /> 100% Confidential
                    </span>
                    <span className="text-slate-200">·</span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Zap className="w-3 h-3 text-slate-400" /> Reply in 48hrs
                    </span>
                    <span className="text-slate-200">·</span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <DollarSign className="w-3 h-3 text-slate-400" /> Zero Fees
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
        <span className="text-xs">Scroll to explore</span>
        <div className="w-5 h-8 border border-white/20 rounded-full flex items-start justify-center p-1">
          <div className="w-1 h-2 bg-white/40 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// ============================================
// STATS SCREEN
// ============================================
const StatsScreen = ({ treatment }) => {
  if (!treatment.stats || treatment.stats.length === 0) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-pulse text-slate-400">Loading statistics...</div>
        </div>
      </section>
    );
  }

  const statStyles = [
    { bg: "bg-orange-50", border: "border-orange-200", iconBg: "bg-orange-100", val: "text-orange-700", icon: "text-orange-600" },
    { bg: "bg-blue-50", border: "border-blue-200", iconBg: "bg-blue-100", val: "text-blue-700", icon: "text-blue-600" },
    { bg: "bg-violet-50", border: "border-violet-200", iconBg: "bg-violet-100", val: "text-violet-700", icon: "text-violet-600" },
    { bg: "bg-green-50", border: "border-green-200", iconBg: "bg-green-100", val: "text-green-700", icon: "text-green-600" },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="text-orange-600 font-medium text-xs uppercase tracking-widest">Our track record</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-2 leading-tight">
            Numbers that speak for themselves
          </h2>
          <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
            Two decades of clinical excellence trusted by patients across every corner of India.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {treatment.stats.map((stat, i) => {
            const Icon = stat.icon || Activity;
            const s = statStyles[i % statStyles.length];
            return (
              <div key={i} className={`relative overflow-hidden rounded-2xl border ${s.bg} ${s.border} p-6 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-200`}>
                <div className={`absolute -top-5 -right-5 w-20 h-20 rounded-full ${s.iconBg} opacity-40`} />
                <div className={`relative z-10 w-12 h-12 ${s.iconBg} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <Icon className={`w-5 h-5 ${s.icon}`} />
                </div>
                <div className={`relative z-10 text-3xl sm:text-4xl font-bold ${s.val} mb-1.5 leading-none`}>{stat.value}</div>
                <div className="relative z-10 text-slate-500 text-xs font-medium">{stat.label}</div>
              </div>
            );
          })}
        </div>
        <div className="bg-slate-900 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2">India's most trusted medical centre</h3>
            <p className="text-slate-400 text-sm">Setting the standard in patient outcomes since 2016.</p>
          </div>
          <div className="hidden md:block w-px self-stretch bg-white/10" />
          <div className="flex gap-8 shrink-0">
            {[{ val: "50+", lbl: "Hospitals" }, { val: "25+", lbl: "Cities" }, { val: "30+", lbl: "Awards" }].map((s) => (
              <div key={s.lbl} className="text-center">
                <div className="text-3xl font-bold text-orange-400">{s.val}</div>
                <div className="text-slate-500 text-xs mt-1">{s.lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// WHY INDIA SCREEN
// ============================================
const WhyIndiaScreen = ({ paxgrowData }) => {
  const defaultReasons = [
    "Top JCI/NABH accredited hospitals with world-class infrastructure",
    "60-80% lower treatment costs compared to Western countries",
    "No waiting lists - immediate treatment and surgery scheduling",
    "English-speaking medical staff and seamless communication"
  ];
  const reasons = paxgrowData?.whyIndiaReasons?.length ? paxgrowData.whyIndiaReasons : defaultReasons;
  const iconStyles = [
    { icon: Microscope, color: "bg-orange-100 text-orange-600", border: "border-orange-200" },
    { icon: Stethoscope, color: "bg-blue-100 text-blue-600", border: "border-blue-200" },
    { icon: Timer, color: "bg-violet-100 text-violet-600", border: "border-violet-200" },
    { icon: Globe, color: "bg-green-100 text-green-600", border: "border-green-200" },
  ];

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-orange-600 font-semibold text-xs uppercase tracking-widest">
            {paxgrowData?.whyIndiaSubtitle || "WHY INDIA"}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4 leading-tight">
            {paxgrowData?.whyIndiaTitle || "World-Class Medical Care at Affordable Costs"}
          </h2>
          <p className="text-slate-500 text-sm max-w-lg mx-auto">
            Top-tier hospitals. No waiting lists. Expert surgeons. At a fraction of Western costs.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {reasons.map((reason, i) => {
            const style = iconStyles[i % iconStyles.length];
            const Icon = style.icon;
            return (
              <div key={i} className={`bg-white rounded-2xl border ${style.border} p-6 hover:shadow-md transition-all`}>
                <div className={`w-11 h-11 rounded-xl ${style.color} flex items-center justify-center mb-4`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-slate-700 text-sm font-medium leading-relaxed">{reason}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================
// PROCESS SCREEN
// ============================================
const ProcessScreen = () => (
  <section className="py-16 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <span className="text-orange-600 font-semibold text-xs uppercase tracking-widest">THE PROCESS</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4 leading-tight">
          From First WhatsApp<br />to Safe Return Home
        </h2>
        <p className="text-slate-500 text-sm max-w-md mx-auto">
          PaxGrow manages every step so you can focus entirely on getting better.
        </p>
      </div>
      <div className="relative max-w-3xl mx-auto">
        <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gradient-to-b from-orange-300 via-violet-300 to-rose-300 hidden sm:block" />
        <div className="space-y-6">
          {PROCESS_STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.num} className="relative flex gap-5 sm:gap-8">
                <div className="relative z-10 shrink-0">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-slate-900 text-white rounded-full text-[10px] font-bold flex items-center justify-center">
                    {step.num}
                  </div>
                </div>
                <div className="flex-1 bg-slate-50 border border-slate-100 rounded-2xl p-5 hover:border-orange-200 hover:shadow-sm transition-all">
                  <h3 className="font-semibold text-slate-900 text-sm mb-2">{step.title}</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-10 text-center">
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white rounded-full px-8 py-4 font-semibold text-sm transition-all shadow-lg shadow-green-500/20"
        >
          <MessageCircle className="w-5 h-5 fill-white" />
          Start with Step 1 — Send Your Reports
        </a>
        <p className="text-slate-400 text-xs mt-3">Free review · No commitment · Reply within 48 hours</p>
      </div>
    </div>
  </section>
);

// ============================================
// CATEGORISED CONDITIONS SCREEN
// ============================================
const CategorisedConditionsScreen = ({ paxgrowData }) => {
  const cats = paxgrowData?.categorisedConditions;
  if (!cats || cats.length === 0) return null;

  const colorMap = {
    blue:   { header: "bg-blue-600",   dot: "bg-blue-500",   border: "border-blue-100"   },
    amber:  { header: "bg-amber-500",  dot: "bg-amber-500",  border: "border-amber-100"  },
    violet: { header: "bg-violet-600", dot: "bg-violet-500", border: "border-violet-100" },
    green:  { header: "bg-green-600",  dot: "bg-green-500",  border: "border-green-100"  },
    rose:   { header: "bg-rose-600",   dot: "bg-rose-500",   border: "border-rose-100"   },
  };

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="text-orange-600 font-semibold text-xs uppercase tracking-widest">WHAT WE TREAT</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 leading-tight">
            {paxgrowData?.conditionsTitle || "Dental Conditions & Procedures"}
          </h2>
          <p className="text-slate-500 text-sm mt-3 max-w-2xl">
            Our partner clinics in India cover every dental specialty — from routine restorations to full-arch implants and corrective jaw surgery.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
          {cats.map((cat, ci) => {
            const c = colorMap[cat.color] || colorMap.blue;
            return (
              <div key={ci} className={`bg-white rounded-3xl border ${c.border} shadow-sm overflow-hidden hover:shadow-md transition-shadow`}>
                <div className={`${c.header} px-5 py-4 flex items-center gap-3`}>
                  <span className="text-2xl">{cat.icon}</span>
                  <h3 className="text-white font-bold text-sm leading-snug">{cat.category}</h3>
                </div>
                <ul className="divide-y divide-slate-100">
                  {cat.items.map((item, ii) => (
                    <li key={ii} className="px-5 py-4 flex items-start gap-3 hover:bg-slate-50 transition-colors">
                      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${c.dot}`} />
                      <div>
                        <p className="text-slate-800 font-semibold text-sm leading-snug">{item.name}</p>
                        {item.desc && (
                          <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl px-6 py-5">
          <div className="w-11 h-11 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
            <Plane className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="text-orange-600 font-semibold text-xs uppercase tracking-widest">Typical India Stay</span>
            <p className="text-slate-700 font-semibold text-sm mt-0.5">
              {paxgrowData?.stayValue || "3–21 days depending on treatment complexity"}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// TREATMENT PHOTOS SCREEN
// ============================================
const TreatmentPhotosScreen = ({ treatment }) => {
  const photos = treatment?.treatmentPhotos;
  if (!photos || photos.length === 0) return null;

  const tagColors = {
    "Endodontics":         { bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500"   },
    "Cosmetic Dentistry":  { bg: "bg-amber-100",  text: "text-amber-700",  dot: "bg-amber-500"  },
    "Aesthetic Dentistry": { bg: "bg-violet-100", text: "text-violet-700", dot: "bg-violet-500" },
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-orange-600 font-semibold text-xs uppercase tracking-widest">Treatment Gallery</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-3 leading-tight">
            Real Procedures. Real Results.
          </h2>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            A snapshot of the dental treatments performed at our partner clinics across India.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {photos.map((photo, i) => {
            const tagStyle = tagColors[photo.tag] || {
              bg: "bg-orange-100",
              text: "text-orange-700",
              dot: "bg-orange-500",
            };
            return (
              <div key={i} className="group relative rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-slate-100">
                <div className="relative h-64 sm:h-72 overflow-hidden bg-slate-100">
                  <img
                    src={photo.src}
                    alt={photo.label}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentElement.classList.add("flex", "items-center", "justify-center");
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="bg-white/95 backdrop-blur rounded-2xl px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
                    <span className="text-slate-800 font-semibold text-sm leading-tight">{photo.label}</span>
                    <span className={`inline-flex items-center gap-1.5 ${tagStyle.bg} ${tagStyle.text} rounded-full px-3 py-1 text-xs font-semibold shrink-0`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${tagStyle.dot}`} />
                      {photo.tag}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-10 text-center">
          <p className="text-slate-500 text-sm mb-4">Want to know if this treatment is right for you?</p>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 bg-green-500 hover:bg-green-600 text-white rounded-full px-8 py-4 font-semibold text-sm transition-all shadow-lg shadow-green-500/20"
          >
            <MessageCircle className="w-4 h-4 fill-white" />
            Send Your Reports — Free Review
          </a>
        </div>
      </div>
    </section>
  );
};

// ============================================
// COST COMPARISON SCREEN
// ============================================
const CostComparisonScreen = ({ paxgrowData }) => {
  const costRows = paxgrowData?.costRows || [];
  if (costRows.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <span className="text-orange-600 font-semibold text-xs uppercase tracking-widest">COST COMPARISON</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 leading-tight">
            {paxgrowData?.costTitle || "Affordable Treatment in India"}
          </h2>
        </div>
        <div className="hidden sm:block rounded-2xl overflow-hidden border border-slate-200 shadow-sm mb-4">
          <div className="grid grid-cols-4 bg-orange-600 text-white text-xs font-bold uppercase tracking-wider">
            <div className="px-6 py-4">Procedure</div>
            <div className="px-6 py-4 text-center">🇮🇳 India (PaxGrow)</div>
            <div className="px-6 py-4 text-center">Africa (local)</div>
            <div className="px-6 py-4 text-center">UK / USA</div>
          </div>
          {costRows.map((row, i) => (
            <div key={i} className={`grid grid-cols-4 border-t border-slate-100 hover:bg-orange-50/40 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}>
              <div className="px-6 py-5 text-slate-700 text-sm font-medium">{row.procedure}</div>
              <div className="px-6 py-5 text-center">
                <span className="text-orange-600 font-bold text-sm">{row.india}</span>
              </div>
              <div className="px-6 py-5 text-center text-slate-500 text-sm">{row.africa}</div>
              <div className="px-6 py-5 text-center text-slate-500 text-sm">{row.ukusa}</div>
            </div>
          ))}
        </div>
        <div className="sm:hidden space-y-4 mb-4">
          {costRows.map((row, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <p className="text-slate-800 font-semibold text-sm mb-4">{row.procedure}</p>
              <div className="space-y-2.5">
                {[
                  { label: "🇮🇳 India (PaxGrow)", val: row.india, highlight: true },
                  { label: "Africa (local)", val: row.africa, highlight: false },
                  { label: "UK / USA", val: row.ukusa, highlight: false },
                ].map((r) => (
                  <div key={r.label} className="flex justify-between items-center">
                    <span className="text-slate-500 text-xs">{r.label}</span>
                    <span className={`text-sm font-semibold ${r.highlight ? "text-orange-600" : "text-slate-600"}`}>{r.val}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-slate-400 text-xs mb-5">
          All costs are approximate and verified from hospital packages (Q1 2026). Final quote provided after medical report review.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {["NABH", "JCI"].map((badge) => (
            <span key={badge} className="bg-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold px-4 py-1.5 rounded-full">
              {badge}
            </span>
          ))}
          <span className="text-slate-400 text-xs">
            All PaxGrow partner hospitals hold NABH or JCI accreditation — verifiable at nabh.co
          </span>
        </div>
      </div>
    </section>
  );
};

// ============================================
// WHY CHOOSE SCREEN
// ============================================
const WhyChooseScreen = ({ treatment }) => {
  const benefits = treatment.benefits || [
    "World-class infrastructure and advanced medical technology",
    "Internationally trained surgeons with decades of experience",
    "Comprehensive pre and post-operative care",
    "Affordable packages with no hidden costs"
  ];

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-widest">Treatment Benefits</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">
              Why Choose {treatment.shortName || treatment.name}?
            </h2>
            <p className="text-slate-500 mb-7 leading-relaxed text-sm">
              Our advanced approach to {(treatment.shortName || treatment.name).toLowerCase()} ensures the best possible outcome.
            </p>
            <div className="space-y-3">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-4 bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md hover:border-orange-100 transition-all group">
                  <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-orange-600 transition-colors">
                    <CheckCircle className="w-4 h-4 text-orange-600 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-slate-700 text-sm font-medium pt-1">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-5">
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-8 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-bold text-lg">Top Rated Treatment</div>
                  <div className="text-orange-200 text-xs">By India's Medical Association</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Experience", value: "15+ Years" },
                  { label: "Patients", value: "10k+" },
                  { label: "Experts", value: "50+" },
                  { label: "Centers", value: "100+" },
                ].map((item) => (
                  <div key={item.label} className="bg-white/10 rounded-2xl p-4 text-center">
                    <div className="text-2xl font-bold">{item.value}</div>
                    <div className="text-orange-200 text-xs mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8">
              <h4 className="font-bold text-2xl text-slate-900 mb-3 leading-tight">
                Not Sure If You Need <span className="text-orange-600">Treatment</span>?
              </h4>
              <div className="space-y-2 mb-6">
                {[
                  "Get a free opinion from our expert dental specialists",
                  "Zero cost, zero commitment — just expert advice",
                ].map((txt, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                    <p className="text-slate-600 text-sm">{txt}</p>
                  </div>
                ))}
              </div>
              <ConsultationModal
                trigger={
                  <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-xl py-5 font-bold text-sm mb-3">
                    📞 Get Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                }
              />
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full border-2 border-green-500 text-green-600 hover:bg-green-50 rounded-xl py-5 font-bold text-sm">
                  <MessageCircle className="w-4 h-4 mr-2 fill-green-500 text-green-500" />
                  Chat on WhatsApp
                </Button>
              </a>
              <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-slate-100">
                {[
                  { v: "50K+", l: "Patients Treated" },
                  { v: "98%", l: "Success Rate" },
                  { v: "24/7", l: "Support Available" },
                ].map((s, i) => (
                  <div key={i} className={`text-center ${i === 1 ? "border-x border-slate-100" : ""}`}>
                    <div className="text-orange-500 font-bold text-lg">{s.v}</div>
                    <div className="text-xs text-slate-500">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// HOW IT WORKS SCREEN
// ============================================
const HowItWorksScreen = ({ treatment }) => {
  const procedure = treatment.procedure || [
    { icon: FileText, title: "Share Medical Reports", duration: "24-48 hrs" },
    { icon: Video, title: "Video Consultation", duration: "Same week" },
    { icon: Plane, title: "Travel & Visa", duration: "3-5 days" },
    { icon: HeartPulse, title: "Treatment & Recovery", duration: "Varies" },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-orange-600 font-semibold text-sm uppercase tracking-widest">Step by Step</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">How It Works</h2>
          <p className="text-slate-500 max-w-xl mx-auto text-sm">
            From your first consultation to complete recovery, we guide you every step of the way.
          </p>
        </div>
        <div className="relative">
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-200 via-orange-400 to-orange-200 mx-32" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {procedure.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="relative flex flex-col items-center text-center group">
                  <div className="relative z-10 mb-5">
                    <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-200 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-slate-900 text-white rounded-full text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-5 w-full border border-slate-100 group-hover:border-orange-200 group-hover:shadow-md transition-all">
                    <h3 className="font-bold text-slate-900 mb-2 text-sm">{step.title}</h3>
                    <div className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 rounded-full px-3 py-1 text-xs font-semibold">
                      <Clock className="w-3 h-3" />
                      {step.duration}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-12 bg-slate-50 rounded-3xl p-7 border border-slate-100 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <div className="font-bold text-slate-900 text-sm">Have questions about the procedure?</div>
              <div className="text-slate-500 text-xs">Our care coordinators are available 24/7</div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <ConsultationModal
              trigger={
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-7 py-5 font-semibold text-sm">
                  Book Now <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              }
            />
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-2 border-green-500 text-green-600 hover:bg-green-50 rounded-full px-7 py-5 font-semibold text-sm w-full sm:w-auto">
                <MessageCircle className="w-4 h-4 mr-2 fill-green-500 text-green-500" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// TESTIMONIALS SCREEN
// ============================================
const TestimonialsScreen = ({ treatment }) => {
  const testimonials = treatment.testimonials || [];
  if (testimonials.length === 0) return null;

  return (
    <section className="py-16 bg-slate-900 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-orange-400 font-semibold text-sm uppercase tracking-widest">Real Stories</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">Patient Success Stories</h2>
          <p className="text-slate-400 max-w-xl mx-auto text-sm">
            Hear from real patients who transformed their lives with our care.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
          <div className="text-center">
            <div className="text-6xl font-bold text-white">4.9</div>
            <div className="flex justify-center mt-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <div className="text-slate-400 text-sm mt-1">Overall Rating</div>
          </div>
          <div className="w-px h-16 bg-slate-700 hidden sm:block" />
          <div className="flex gap-8">
            {[
              { label: "Total Reviews", val: "3,000+" },
              { label: "Recommend Us", val: "99%" },
              { label: "5-Star Reviews", val: "92%" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl font-bold text-orange-400">{s.val}</div>
                <div className="text-slate-400 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-slate-800 rounded-3xl p-6 border border-slate-700 hover:border-orange-500/50 transition-colors">
              <div className="text-4xl text-orange-500 font-serif leading-none mb-4">"</div>
              <div className="flex mb-4">
                {[...Array(t.rating || 5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-5 italic">{t.text}</p>
              <div className="inline-block bg-orange-500/20 text-orange-400 rounded-full px-3 py-1 text-xs font-medium mb-5">
                {t.treatment}
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-slate-700">
                <img
                  src={t.image || DEFAULT_AVATAR}
                  alt={t.name}
                  className="w-11 h-11 rounded-full object-cover ring-2 ring-orange-500/30"
                  loading="lazy"
                  onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                />
                <div>
                  <div className="font-semibold text-white text-sm">{t.name}</div>
                  <div className="text-slate-400 text-xs">Age {t.age} · {t.location} · {t.date}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <p className="text-slate-400 text-sm mb-5">Ready to write your own success story?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <ConsultationModal
              trigger={
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 font-semibold">
                  Get Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              }
            />
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-2 border-green-500 text-green-600 hover:bg-green-50 rounded-full px-8 py-6 font-semibold">
                <MessageCircle className="w-4 h-4 mr-2 fill-green-500 text-green-500" />
                Chat on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FAQ SCREEN
// ============================================
const FAQScreen = ({ treatment }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const faqs = treatment.faqs || [];
  const toggleFaq = (idx) => setOpenIdx(openIdx === idx ? null : idx);
  if (faqs.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div className="lg:sticky lg:top-24">
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-widest">FAQ</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-500 leading-relaxed mb-7 text-sm">
              Everything you need to know about {treatment.name}.
            </p>
            <div className="bg-gradient-to-br from-slate-50 to-orange-50 border border-orange-100 rounded-3xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-green-100 rounded-xl flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm">Still have questions?</div>
                  <div className="text-slate-500 text-xs">We're here to help</div>
                </div>
              </div>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white rounded-xl py-3.5 font-semibold text-sm transition-colors shadow-md shadow-green-500/20"
              >
                <MessageCircle className="w-4 h-4 fill-white" />
                Chat on WhatsApp
              </a>
              <div className="flex items-center justify-between pt-2 border-t border-orange-100">
                {[
                  { icon: Timer, label: "48hr reply" },
                  { icon: DollarSign, label: "Free advice" },
                  { icon: Lock, label: "Confidential" },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <div className="w-8 h-8 bg-white rounded-lg border border-slate-200 flex items-center justify-center shadow-sm">
                        <Icon className="w-3.5 h-3.5 text-orange-500" />
                      </div>
                      <span className="text-slate-500 text-[10px] font-medium">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`border rounded-2xl overflow-hidden transition-all ${openIdx === i ? "border-orange-200 shadow-md" : "border-slate-200"}`}>
                <button
                  onClick={() => toggleFaq(i)}
                  className={`w-full flex items-center justify-between p-5 text-left transition-colors ${openIdx === i ? "bg-orange-50" : "bg-white hover:bg-slate-50"}`}
                >
                  <span className="font-semibold text-slate-900 text-sm pr-4 leading-relaxed">{faq.q}</span>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${openIdx === i ? "bg-orange-600" : "bg-slate-100"}`}>
                    {openIdx === i
                      ? <ChevronUp className="w-4 h-4 text-white" />
                      : <ChevronDown className="w-4 h-4 text-slate-600" />
                    }
                  </div>
                </button>
                {openIdx === i && (
                  <div className="px-5 pb-5 bg-orange-50 border-t border-orange-100">
                    <p className="text-slate-600 text-sm leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FREE MEDICAL REVIEW CTA
// ============================================
const FreeMedicalReviewScreen = () => (
  <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
    </div>
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 text-green-400 rounded-full px-4 py-1.5 text-xs font-semibold mb-6">
        <Sparkles className="w-3.5 h-3.5" />
        Free · No Commitment · 48hr Response
      </div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
        Ready to get a free<br />
        <span className="text-orange-400">dental review?</span>
      </h2>
      <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
        Send your dental X-rays or photos on WhatsApp. Within 48 hours, Samar personally reviews your case and sends you a plain-English summary with treatment options and cost estimates.{" "}
        <span className="text-white font-semibold">Zero charge. Zero commitment.</span>
      </p>
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white rounded-2xl px-8 py-5 font-bold text-base sm:text-lg transition-all shadow-xl shadow-green-500/25 hover:shadow-green-500/40 hover:-translate-y-0.5 mb-5"
      >
        <MessageCircle className="w-6 h-6 fill-white" />
        Send X-rays on WhatsApp
      </a>
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-400 mb-10">
        {["Free review", "No commitment", "Reply within 48 hours", "Zero coordination fees"].map((item, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            {item}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { icon: FileText, bg: "bg-orange-500/20", border: "border-orange-500/30", color: "text-orange-400", label: "Upload any format", sub: "PDF, image, X-ray" },
          { icon: Timer, bg: "bg-blue-500/20", border: "border-blue-500/30", color: "text-blue-400", label: "48-hour response", sub: "Personal review" },
          { icon: BadgeCheck, bg: "bg-violet-500/20", border: "border-violet-500/30", color: "text-violet-400", label: "NABH Accredited", sub: "Dental centres" },
          { icon: Globe, bg: "bg-green-500/20", border: "border-green-500/30", color: "text-green-400", label: "Visa guidance", sub: "Included free" },
        ].map((card, i) => {
          const Icon = card.icon;
          return (
            <div key={i} className={`${card.bg} border ${card.border} rounded-2xl p-4 text-center`}>
              <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center mx-auto mb-3`}>
                <Icon className={`w-5 h-5 ${card.color}`} />
              </div>
              <div className="text-white font-semibold text-sm mb-0.5">{card.label}</div>
              <div className="text-slate-400 text-xs">{card.sub}</div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// ============================================
// FLOATING CTA
// ============================================
const FloatingCTA = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 md:hidden bg-white border-t border-slate-200 px-4 py-3 shadow-xl">
      <div className="flex gap-3">
        <ConsultationModal
          trigger={
            <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-xl py-5 font-semibold text-sm">
              Free Consultation
            </Button>
          }
        />
        <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="flex-1">
          <Button variant="outline" className="w-full border-2 border-green-500 text-green-600 rounded-xl py-5 font-semibold text-sm">
            <MessageCircle className="w-4 h-4 mr-1.5 fill-green-500 text-green-500" />
            WhatsApp
          </Button>
        </a>
      </div>
    </div>
  );
};

// ============================================
// MAIN TEMPLATE
// ============================================
const TreatmentTemplateForDental = ({ treatment, paxgrowData }) => {
  const safeTreatment = {
    name: "Medical Treatment",
    shortName: "Treatment",
    tagline: "Advanced Medical Care in India",
    heroImage: "/images/default-hero.jpg",
    heroSubtag: "Save up to 80% compared to Western countries",
    description: "World-class medical treatment at affordable costs with top JCI/NABH accredited hospitals.",
    category: "Medical Tourism",
    badge: "Premium Care",
    stats: [],
    benefits: [],
    procedure: [],
    testimonials: [],
    faqs: [],
    costCard: null,
    treatmentPhotos: [],   // ✅ included so TreatmentPhotosScreen works
    slug: "",
    ...treatment,          // ✅ spreads all treatment data including treatmentPhotos
  };

  return (
    <div className="bg-white">
      <HeroScreen treatment={safeTreatment} />
      <StatsScreen treatment={safeTreatment} />
      <WhyIndiaScreen paxgrowData={paxgrowData} />
      <ProcessScreen />
      <CategorisedConditionsScreen paxgrowData={paxgrowData} />
      <TreatmentPhotosScreen treatment={safeTreatment} />  {/* ✅ correct prop */}
      <CostComparisonScreen paxgrowData={paxgrowData} />
      <WhyChooseScreen treatment={safeTreatment} />
      <HowItWorksScreen treatment={safeTreatment} />
      <TestimonialsScreen treatment={safeTreatment} />
      <FAQScreen treatment={safeTreatment} />
      <FreeMedicalReviewScreen />
      <FloatingCTA />
    </div>
  );
};

// ✅ PropTypes reference the correct component name
TreatmentTemplateForDental.propTypes = {
  treatment: PropTypes.shape({
    name: PropTypes.string,
    shortName: PropTypes.string,
    tagline: PropTypes.string,
    heroImage: PropTypes.string,
    heroSubtag: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.string,
    badge: PropTypes.string,
    stats: PropTypes.array,
    benefits: PropTypes.array,
    procedure: PropTypes.array,
    testimonials: PropTypes.array,
    faqs: PropTypes.array,
    costCard: PropTypes.object,
    treatmentPhotos: PropTypes.array,
    slug: PropTypes.string,
  }),
  paxgrowData: PropTypes.shape({
    whyIndiaSubtitle: PropTypes.string,
    whyIndiaTitle: PropTypes.string,
    whyIndiaReasons: PropTypes.array,
    categorisedConditions: PropTypes.array,
    conditionsTitle: PropTypes.string,
    stayValue: PropTypes.string,
    costTitle: PropTypes.string,
    costRows: PropTypes.array,
  }),
};

export default TreatmentTemplateForDental;